import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import AppShell, { Protected } from "@/components/AppShell";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DownloadSimple, ChartBar, Users, TrendUp, Sigma, ArrowsHorizontal, ChartLine, Sparkle, FileMagnifyingGlass, CaretDown, Funnel } from "@phosphor-icons/react";
import { toast } from "sonner";
import { log } from "@/lib/logger";
import DocumentAnalyzer from "@/pages/panels/DocumentAnalyzer";
import {
  BarChart, Bar, LineChart, Line, ScatterChart, Scatter, ReferenceLine,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend,
} from "recharts";

const PALETTE = ["#3A8FA5", "#3A8FA5", "#8FB09E", "#D45A3F", "#B9502C", "#E8C09E", "#5F758F", "#A9B7B0"];
const EMPTY_FILTERS = {
  template_id: "", date_from: "", date_to: "", sex: "all", age_from: "", age_to: "",
  // Inclusion / exclusion
  score_min: "", score_max: "",
  interp_mode: "include",   // "include" | "exclude"
  interp_labels: [],        // string[]
  complete_only: false,
};

function Kpi({ label, value, icon: Icon, hint }) {
  return (
    <div className="bg-white border border-[#E1E9F0] rounded-2xl p-5">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">{label}</p>
        <Icon size={18} weight="duotone" className="text-[#3A8FA5]" />
      </div>
      <p className="text-3xl font-bold text-[#1E3A5F] leading-none tracking-tight tabular-nums">{value ?? "—"}</p>
      {hint && <p className="text-xs text-[#5F758F] mt-2">{hint}</p>}
    </div>
  );
}

function AdvancedCriteria({ filters, onChange, currentTemplate }) {
  const [open, setOpen] = useState(false);
  const set = (patch) => onChange({ ...filters, ...patch });
  const interp = (currentTemplate?.scoring?.interpretation) || [];
  const toggle = (label) => {
    const next = filters.interp_labels?.includes(label)
      ? filters.interp_labels.filter((l) => l !== label)
      : [...(filters.interp_labels || []), label];
    set({ interp_labels: next });
  };
  const active =
    (filters.score_min !== "" && filters.score_min !== undefined) ||
    (filters.score_max !== "" && filters.score_max !== undefined) ||
    (filters.interp_labels && filters.interp_labels.length > 0) ||
    filters.complete_only;
  return (
    <div className="mt-3 border-t border-[#E1E9F0] pt-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 text-[12px] font-semibold tracking-[0.08em] uppercase text-[#3A8FA5] hover:text-[#164E5F]"
        data-testid="toggle-advanced-criteria"
      >
        <Funnel size={13} weight="duotone" />
        Critères d'inclusion / exclusion
        {active && (
          <span className="inline-flex items-center rounded-full bg-[#E6F1F5] text-[#164E5F] border border-[#C7DEE6] px-2 py-0.5 text-[10px] tracking-widest">
            actifs
          </span>
        )}
        <CaretDown size={11} weight="bold" className={`ml-auto transition-transform ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && (
        <div className="mt-3 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Score total min</Label>
              <Input
                type="number" step="0.1" placeholder="—"
                value={filters.score_min ?? ""}
                onChange={(e) => set({ score_min: e.target.value })}
                className="mt-2 bg-white border-[#E1E9F0]"
                data-testid="adv-score-min"
              />
            </div>
            <div>
              <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Score total max</Label>
              <Input
                type="number" step="0.1" placeholder="—"
                value={filters.score_max ?? ""}
                onChange={(e) => set({ score_max: e.target.value })}
                className="mt-2 bg-white border-[#E1E9F0]"
                data-testid="adv-score-max"
              />
            </div>
          </div>
          {interp.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Interprétation clinique</Label>
                <div className="inline-flex bg-[#F4F7FB] border border-[#E1E9F0] rounded-full p-0.5 text-[11px]">
                  <button
                    type="button"
                    onClick={() => set({ interp_mode: "include" })}
                    className={`px-2.5 py-1 rounded-full font-semibold transition-colors ${filters.interp_mode === "include" ? "bg-white text-[#164E5F] shadow-sm" : "text-[#5F758F]"}`}
                    data-testid="adv-interp-include"
                  >
                    Inclure
                  </button>
                  <button
                    type="button"
                    onClick={() => set({ interp_mode: "exclude" })}
                    className={`px-2.5 py-1 rounded-full font-semibold transition-colors ${filters.interp_mode === "exclude" ? "bg-white text-[#8B2A1E] shadow-sm" : "text-[#5F758F]"}`}
                    data-testid="adv-interp-exclude"
                  >
                    Exclure
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {interp.map((it) => {
                  const label = it.label || `${it.min}-${it.max}`;
                  const on = filters.interp_labels?.includes(label);
                  return (
                    <button
                      type="button"
                      key={label}
                      onClick={() => toggle(label)}
                      data-testid={`adv-interp-${label}`}
                      className={`px-3 py-1.5 rounded-full text-[12px] font-semibold border transition-colors ${on
                        ? filters.interp_mode === "exclude"
                          ? "bg-[#FFE0DC] border-[#8B2A1E] text-[#8B2A1E]"
                          : "bg-[#E6F1F5] border-[#3A8FA5] text-[#164E5F]"
                        : "bg-white border-[#E1E9F0] text-[#5F758F] hover:border-[#C7DEE6]"}`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <label className="flex items-center gap-2 text-[13px] text-[#1E3A5F] cursor-pointer select-none">
            <input
              type="checkbox"
              checked={!!filters.complete_only}
              onChange={(e) => set({ complete_only: e.target.checked })}
              className="rounded border-[#E1E9F0]"
              data-testid="adv-complete-only"
            />
            Réponses complètes uniquement (exclure les brouillons)
          </label>
        </div>
      )}
    </div>
  );
}

function FiltersRow({ filters, onChange, templates, dense = false, label = null }) {
  const set = (patch) => onChange({ ...filters, ...patch });
  if (dense) {
    return (
      <div className="space-y-3">
        {label && <p className="text-[11px] tracking-[0.12em] uppercase text-[#3A8FA5] font-bold">{label}</p>}
        <div>
          <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Questionnaire</Label>
          <Select value={filters.template_id} onValueChange={(v) => set({ template_id: v })}>
            <SelectTrigger className="mt-2 bg-white border-[#E1E9F0]"><SelectValue placeholder="Choisir…" /></SelectTrigger>
            <SelectContent>
              {templates.map((t) => (
                <SelectItem key={t.template_id} value={t.template_id}>
                  <span className="flex items-center gap-2">
                    <span>{t.title}</span>
                    {t.is_deleted && (
                      <span className="inline-flex items-center rounded-full bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 text-[10px] tracking-[0.14em] uppercase font-semibold italic">Supprimé</span>
                    )}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Du</Label>
            <Input type="date" value={filters.date_from} onChange={(e) => set({ date_from: e.target.value })} className="mt-2 bg-white border-[#E1E9F0]" />
          </div>
          <div>
            <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Au</Label>
            <Input type="date" value={filters.date_to} onChange={(e) => set({ date_to: e.target.value })} className="mt-2 bg-white border-[#E1E9F0]" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Sexe</Label>
            <Select value={filters.sex} onValueChange={(v) => set({ sex: v })}>
              <SelectTrigger className="mt-2 bg-white border-[#E1E9F0]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="F">Femme</SelectItem>
                <SelectItem value="M">Homme</SelectItem>
                <SelectItem value="O">Autre</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Âge min</Label>
              <Input type="number" min="0" max="120" value={filters.age_from} onChange={(e) => set({ age_from: e.target.value })} className="mt-2 bg-white border-[#E1E9F0]" />
            </div>
            <div>
              <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">max</Label>
              <Input type="number" min="0" max="120" value={filters.age_to} onChange={(e) => set({ age_to: e.target.value })} className="mt-2 bg-white border-[#E1E9F0]" />
            </div>
          </div>
        </div>
        <AdvancedCriteria filters={filters} onChange={onChange} currentTemplate={templates.find((t) => t.template_id === filters.template_id)} />
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
      {label && <div className="col-span-2 md:col-span-6"><p className="text-[11px] tracking-[0.12em] uppercase text-[#3A8FA5] font-bold">{label}</p></div>}
      <div className="col-span-2">
        <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Questionnaire</Label>
        <Select value={filters.template_id} onValueChange={(v) => set({ template_id: v })}>
          <SelectTrigger className="mt-2 bg-white border-[#E1E9F0]"><SelectValue placeholder="Choisir…" /></SelectTrigger>
          <SelectContent>
            {templates.map((t) => (
              <SelectItem key={t.template_id} value={t.template_id}>
                <span className="flex items-center gap-2">
                  <span>{t.title}</span>
                  {t.is_deleted && (
                    <span className="inline-flex items-center rounded-full bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 text-[10px] tracking-[0.14em] uppercase font-semibold italic">Supprimé</span>
                  )}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Du</Label>
        <Input type="date" value={filters.date_from} onChange={(e) => set({ date_from: e.target.value })} className="mt-2 bg-white border-[#E1E9F0]" />
      </div>
      <div>
        <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Au</Label>
        <Input type="date" value={filters.date_to} onChange={(e) => set({ date_to: e.target.value })} className="mt-2 bg-white border-[#E1E9F0]" />
      </div>
      <div>
        <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Sexe</Label>
        <Select value={filters.sex} onValueChange={(v) => set({ sex: v })}>
          <SelectTrigger className="mt-2 bg-white border-[#E1E9F0]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous</SelectItem>
            <SelectItem value="F">Femme</SelectItem>
            <SelectItem value="M">Homme</SelectItem>
            <SelectItem value="O">Autre</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Âge min</Label>
          <Input type="number" min="0" max="120" value={filters.age_from} onChange={(e) => set({ age_from: e.target.value })} className="mt-2 bg-white border-[#E1E9F0]" />
        </div>
        <div>
          <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">max</Label>
          <Input type="number" min="0" max="120" value={filters.age_to} onChange={(e) => set({ age_to: e.target.value })} className="mt-2 bg-white border-[#E1E9F0]" />
        </div>
      </div>
      <div className="col-span-2 md:col-span-6">
        <AdvancedCriteria filters={filters} onChange={onChange} currentTemplate={templates.find((t) => t.template_id === filters.template_id)} />
      </div>
    </div>
  );
}

// Serialize a filters object into query params — used by both /studies/stats,
// /studies/export.csv and /studies/correlation. Empty strings are stripped so
// FastAPI defaults kick in server-side.
function serializeFilters(filters) {
  const params = { template_id: filters.template_id };
  if (filters.date_from) params.date_from = filters.date_from;
  if (filters.date_to) params.date_to = filters.date_to;
  if (filters.sex && filters.sex !== "all") params.sex = filters.sex;
  if (filters.age_from !== "") params.age_from = filters.age_from;
  if (filters.age_to !== "") params.age_to = filters.age_to;
  if (filters.score_min !== "" && filters.score_min !== undefined) params.score_min = filters.score_min;
  if (filters.score_max !== "" && filters.score_max !== undefined) params.score_max = filters.score_max;
  if (filters.complete_only) params.complete_only = "true";
  if (Array.isArray(filters.interp_labels) && filters.interp_labels.length) {
    const key = filters.interp_mode === "exclude" ? "interpretation_not_in" : "interpretation_in";
    // FastAPI multi-value: repeat the key
    params[key] = filters.interp_labels;
  }
  return params;
}

async function fetchStats(filters) {
  if (!filters.template_id) return null;
  const { data } = await api.get("/studies/stats", { params: serializeFilters(filters), paramsSerializer: { indexes: null } });
  return data;
}

function exportCsvUrl(filters) {
  const params = new URLSearchParams();
  const src = serializeFilters(filters);
  Object.entries(src).forEach(([k, v]) => {
    if (Array.isArray(v)) v.forEach((x) => params.append(k, x));
    else params.set(k, v);
  });
  return `${API}/studies/export.csv?${params}`;
}

// ---------------------------------------------------------------------------
// Correlation view — test statistique entre deux variables d'une cohorte.
// Le panneau réutilise `filtersA` (donc les critères d'inclusion/exclusion
// appliqués dans l'onglet "Vue simple" restent effectifs), et ajoute deux
// axes libres (score total, âge, ou chaque sous-score du template).
// ---------------------------------------------------------------------------
function CorrelationView({ filters, templates }) {
  const currentTpl = templates.find((t) => t.template_id === filters.template_id);
  const dims = ((currentTpl?.scoring?.dimensions) || []);
  const varOptions = useMemo(() => [
    { value: "total", label: "Score total" },
    { value: "age", label: "Âge du patient" },
    ...dims.map((d) => ({ value: `dim:${d.key}`, label: `Sous-score · ${d.label || d.key}` })),
  ], [dims]);
  const [xVar, setXVar] = useState("total");
  const [yVar, setYVar] = useState("age");
  const [method, setMethod] = useState("auto");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const req = useRef(0);

  useEffect(() => {
    if (!filters.template_id || !xVar || !yVar || xVar === yVar) { setResult(null); return; }
    const ticket = ++req.current;
    setLoading(true);
    (async () => {
      try {
        const params = { ...serializeFilters(filters), x: xVar, y: yVar, method };
        const { data } = await api.get("/studies/correlation", { params, paramsSerializer: { indexes: null } });
        if (ticket === req.current) setResult(data);
      } catch (err) {
        if (ticket !== req.current) return;
        log.error("correlation", err);
        toast.error(err?.response?.data?.detail || "Erreur corrélation");
        setResult(null);
      } finally {
        if (ticket === req.current) setLoading(false);
      }
    })();
  }, [filters, xVar, yVar, method]);

  // Regression line: least-squares fit through the scatter (visual aid only)
  const regression = useMemo(() => {
    const pts = result?.points || [];
    if (pts.length < 2) return null;
    const n = pts.length;
    const sumX = pts.reduce((a, p) => a + p.x, 0);
    const sumY = pts.reduce((a, p) => a + p.y, 0);
    const sumXY = pts.reduce((a, p) => a + p.x * p.y, 0);
    const sumXX = pts.reduce((a, p) => a + p.x * p.x, 0);
    const denom = n * sumXX - sumX * sumX;
    if (Math.abs(denom) < 1e-9) return null;
    const slope = (n * sumXY - sumX * sumY) / denom;
    const intercept = (sumY - slope * sumX) / n;
    const xs = pts.map((p) => p.x);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    return [{ x: minX, y: slope * minX + intercept }, { x: maxX, y: slope * maxX + intercept }];
  }, [result]);

  const methodLabel = {
    pearson: "Pearson (r)", spearman: "Spearman (ρ)", kendall: "Kendall (τ)",
  }[result?.method_used] || result?.method_used || "—";

  const xLabel = varOptions.find((v) => v.value === xVar)?.label || xVar;
  const yLabel = varOptions.find((v) => v.value === yVar)?.label || yVar;

  return (
    <>
      <div className="bg-white border border-[#E1E9F0] rounded-2xl p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
        <div>
          <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Variable X</Label>
          <Select value={xVar} onValueChange={setXVar}>
            <SelectTrigger className="mt-2 bg-white border-[#E1E9F0]" data-testid="corr-x"><SelectValue /></SelectTrigger>
            <SelectContent>
              {varOptions.map((v) => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Variable Y</Label>
          <Select value={yVar} onValueChange={setYVar}>
            <SelectTrigger className="mt-2 bg-white border-[#E1E9F0]" data-testid="corr-y"><SelectValue /></SelectTrigger>
            <SelectContent>
              {varOptions.map((v) => <SelectItem key={v.value} value={v.value} disabled={v.value === xVar}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Test</Label>
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger className="mt-2 bg-white border-[#E1E9F0]" data-testid="corr-method"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Auto (recommandé)</SelectItem>
              <SelectItem value="pearson">Pearson (paramétrique)</SelectItem>
              <SelectItem value="spearman">Spearman (rangs)</SelectItem>
              <SelectItem value="kendall">Kendall (τ)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <p className="text-[11px] text-[#5F758F] leading-relaxed">
          <strong className="text-[#164E5F]">Auto</strong> teste la normalité (Shapiro) puis retient Pearson si linéarité, Spearman sinon. Utile en clinique où les échelles ordinales sont fréquentes.
        </p>
      </div>

      {loading && <p className="text-sm text-[#5F758F]">Calcul en cours…</p>}
      {!loading && result && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
            <Kpi label="Coefficient" value={result.r !== null && result.r !== undefined ? result.r.toFixed(3) : "—"} icon={ChartLine} hint={methodLabel} />
            <Kpi label="p-value" value={result.p_value !== null && result.p_value !== undefined ? (result.p_value < 0.001 ? "< 0,001" : result.p_value.toFixed(3)) : "—"} icon={Sigma} hint={result.p_value !== null && result.p_value < 0.05 ? "Significatif" : "Non signif."} />
            <Kpi label="Effectif" value={result.n ?? "—"} icon={Users} hint="Cohorte analysée" />
            <Kpi label="IC 95 % bas" value={result.ci_low !== null && result.ci_low !== undefined ? result.ci_low.toFixed(2) : "—"} icon={ChartBar} />
            <Kpi label="IC 95 % haut" value={result.ci_high !== null && result.ci_high !== undefined ? result.ci_high.toFixed(2) : "—"} icon={ChartBar} />
          </div>
          <div className="bg-[#E6F1F5] border border-[#C7DEE6] rounded-2xl p-4 mb-6 text-sm text-[#164E5F]" data-testid="corr-interpretation">
            <strong className="mr-2">Interprétation :</strong>{result.interpretation}
          </div>
          <div className="bg-white border border-[#E1E9F0] rounded-2xl p-5">
            <h3 className="text-xl font-bold text-[#1E3A5F] mb-1">Nuage de points</h3>
            <p className="text-xs text-[#5F758F] mb-4">{xLabel} × {yLabel}</p>
            {result.points.length > 0 ? (
              <ResponsiveContainer width="100%" height={360}>
                <ScatterChart>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E1E9F0" />
                  <XAxis type="number" dataKey="x" name={xLabel} stroke="#5F758F" tick={{ fontSize: 12 }} label={{ value: xLabel, position: "insideBottom", offset: -5, fill: "#5F758F", fontSize: 12 }} />
                  <YAxis type="number" dataKey="y" name={yLabel} stroke="#5F758F" tick={{ fontSize: 12 }} label={{ value: yLabel, angle: -90, position: "insideLeft", fill: "#5F758F", fontSize: 12 }} />
                  <Tooltip cursor={{ strokeDasharray: "3 3" }} />
                  <Scatter data={result.points} fill="#3A8FA5" fillOpacity={0.6} />
                  {regression && (
                    <Scatter data={regression} line={{ stroke: "#3A8FA5", strokeWidth: 2, strokeDasharray: "6 4" }} shape={() => null} legendType="none" />
                  )}
                </ScatterChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-[#5F758F] text-center py-12">Aucun point à afficher — la cohorte est vide ou les deux variables sont manquantes pour toutes les réponses.</p>
            )}
          </div>
        </>
      )}
    </>
  );
}

function SingleView({ stats, templates, tone = "sage" }) {
  if (!stats) return null;
  const deletedBanner = stats.template?.is_deleted ? (
    <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-[#7F1D1D] flex items-start gap-2" data-testid="deleted-template-banner">
      <span className="mt-0.5">⚠</span>
      <span>
        <strong>Questionnaire supprimé.</strong> Ce modèle n'est plus disponible pour de nouveaux envois — vous consultez ici les réponses historiques.
        {stats.template?.deleted_at && (
          <> Supprimé le {new Date(stats.template.deleted_at).toLocaleDateString("fr-FR")}.</>
        )}
      </span>
    </div>
  ) : null;
  if (stats.count === 0) {
    return (
      <>
        {deletedBanner}
        <div className="bg-white border border-[#E1E9F0] rounded-2xl p-10 text-center">
          <p className="text-xl font-bold text-[#1E3A5F] mb-1">Aucune réponse</p>
          <p className="text-sm text-[#5F758F]">Aucune donnée pour ces critères.</p>
        </div>
      </>
    );
  }
  const barColor = tone === "sage" ? "#3A8FA5" : "#D45A3F";
  const lineColor = tone === "sage" ? "#3A8FA5" : "#B9502C";
  return (
    <>
      {deletedBanner}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Kpi label="Effectif" value={stats.count} icon={Users} />
        <Kpi label="Moyenne" value={stats.score.mean ?? "—"} icon={Sigma} hint={stats.score.max != null ? `sur ${stats.score.max}` : null} />
        <Kpi label="Médiane" value={stats.score.median ?? "—"} icon={ChartBar} hint={stats.score.q1 != null && stats.score.q3 != null ? `Q1 ${stats.score.q1} · Q3 ${stats.score.q3}` : null} />
        <Kpi label="Écart-type" value={stats.score.stddev ?? "—"} icon={TrendUp} hint={stats.score.min != null && stats.score.max != null ? `Min ${stats.score.min} · Max ${stats.score.max}` : null} />
      </div>
      {stats.subscores && stats.subscores.length > 0 && (
        <div className="bg-white border border-[#E1E9F0] rounded-2xl p-4 mb-4" data-testid="stats-subscores">
          <h4 className="text-base font-bold text-[#1E3A5F] mb-3">Sous-scores par dimension</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs tracking-widest uppercase text-[#5F758F] border-b border-[#E1E9F0]">
                  <th className="py-2 pr-4">Dimension</th>
                  <th className="py-2 pr-4 tabular-nums">n</th>
                  <th className="py-2 pr-4 tabular-nums">Moyenne</th>
                  <th className="py-2 pr-4 tabular-nums">Médiane</th>
                  <th className="py-2 pr-4 tabular-nums">Écart-type</th>
                  <th className="py-2 pr-4 tabular-nums">Min–Max</th>
                  <th className="py-2 pr-4">Interprétation dominante</th>
                </tr>
              </thead>
              <tbody>
                {stats.subscores.map((sub) => {
                  const top = (sub.interpretation || [])[0];
                  return (
                    <tr key={sub.key} className="border-b border-[#E1E9F0] last:border-b-0">
                      <td className="py-2 pr-4">
                        <p className="text-[#1E3A5F] font-medium">{sub.label}</p>
                        {sub.max != null && <p className="text-[10px] text-[#5F758F]">sur {sub.max}</p>}
                      </td>
                      <td className="py-2 pr-4 tabular-nums text-[#1E3A5F]">{sub.stats?.n ?? 0}</td>
                      <td className="py-2 pr-4 tabular-nums text-[#1E3A5F]">{sub.stats?.mean ?? "—"}</td>
                      <td className="py-2 pr-4 tabular-nums text-[#1E3A5F]">{sub.stats?.median ?? "—"}</td>
                      <td className="py-2 pr-4 tabular-nums text-[#5F758F]">{sub.stats?.stddev ?? "—"}</td>
                      <td className="py-2 pr-4 tabular-nums text-[#5F758F]">
                        {sub.stats?.min != null && sub.stats?.max != null ? `${sub.stats.min} – ${sub.stats.max}` : "—"}
                      </td>
                      <td className="py-2 pr-4">
                        {top ? (
                          <span className="inline-flex items-center gap-1.5">
                            <span className="text-[#1E3A5F]">{top.label}</span>
                            <span className="text-xs text-[#5F758F]">({top.pct}%)</span>
                          </span>
                        ) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {stats.histogram.length > 0 && (
        <div className="bg-white border border-[#E1E9F0] rounded-2xl p-4 mb-4">
          <h4 className="text-base font-bold text-[#1E3A5F] mb-2">Distribution des scores</h4>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={stats.histogram}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E1E9F0" />
              <XAxis dataKey="label" fontSize={11} stroke="#5F758F" />
              <YAxis fontSize={11} stroke="#5F758F" allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #E1E9F0" }} />
              <Bar dataKey="count" fill={barColor} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      {stats.interpretation.length > 0 && (
        <div className="bg-white border border-[#E1E9F0] rounded-2xl p-4 mb-4">
          <h4 className="text-base font-bold text-[#1E3A5F] mb-2">Interprétation clinique</h4>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={stats.interpretation} dataKey="count" nameKey="label" innerRadius={45} outerRadius={80} paddingAngle={2}>
                {stats.interpretation.map((slice, i) => <Cell key={slice.label} fill={PALETTE[i % PALETTE.length]} />)}
              </Pie>
              <Tooltip formatter={(v, name, ctx) => [`${v} (${ctx.payload.pct}%)`, ctx.payload.label]} contentStyle={{ borderRadius: 6, border: "1px solid #E1E9F0" }} />
              <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
      {stats.timeline.length > 1 && (
        <div className="bg-white border border-[#E1E9F0] rounded-2xl p-4">
          <h4 className="text-base font-bold text-[#1E3A5F] mb-2">Évolution mensuelle</h4>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={stats.timeline}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E1E9F0" />
              <XAxis dataKey="month" fontSize={11} stroke="#5F758F" />
              <YAxis fontSize={11} stroke="#5F758F" />
              <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #E1E9F0" }} />
              <Line type="monotone" dataKey="median" stroke={lineColor} strokeWidth={2} dot={{ r: 3 }} name="Médiane" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </>
  );
}

function ComparisonSummary({ a, b, labels }) {
  if (!a || !b || a.count === 0 || b.count === 0) return null;
  const rows = [
    ["Effectif", a.count, b.count],
    ["Moyenne", a.score.mean, b.score.mean],
    ["Médiane", a.score.median, b.score.median],
    ["Q1", a.score.q1, b.score.q1],
    ["Q3", a.score.q3, b.score.q3],
    ["Écart-type", a.score.stddev, b.score.stddev],
    ["Min", a.score.min, b.score.min],
    ["Max", a.score.max, b.score.max],
  ];
  return (
    <div className="bg-white border border-[#E1E9F0] rounded-2xl p-5 mb-6">
      <h3 className="text-xl font-bold text-[#1E3A5F] mb-4 flex items-center gap-2">
        <ArrowsHorizontal size={18} weight="duotone" className="text-[#3A8FA5]" />
        Synthèse comparative
      </h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs tracking-widest uppercase text-[#5F758F]">
              <th className="py-2 pr-4">Indicateur</th>
              <th className="py-2 pr-4 text-[#3A8FA5]">{labels.a}</th>
              <th className="py-2 pr-4 text-[#B9502C]">{labels.b}</th>
              <th className="py-2 pr-4">Δ</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([label, va, vb]) => {
              const delta = (typeof va === "number" && typeof vb === "number") ? (vb - va) : null;
              return (
                <tr key={label} className="border-t border-[#E1E9F0]">
                  <td className="py-2 pr-4 text-[#1E3A5F]">{label}</td>
                  <td className="py-2 pr-4 tabular-nums">{va ?? "—"}</td>
                  <td className="py-2 pr-4 tabular-nums">{vb ?? "—"}</td>
                  <td className={`py-2 pr-4 tabular-nums ${delta != null ? (delta > 0 ? "text-[#B9502C]" : delta < 0 ? "text-[#3A8FA5]" : "text-[#5F758F]") : "text-[#5F758F]"}`}>
                    {delta != null ? (delta > 0 ? `+${delta.toFixed(2)}` : delta.toFixed(2)) : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Studies() {
  const [templates, setTemplates] = useState([]);
  const [mode, setMode] = useState("single");
  const [filtersA, setFiltersA] = useState(EMPTY_FILTERS);
  const { user } = useAuth();
  const isResearch = (user?.role || "admin") === "research";
  const [filtersB, setFiltersB] = useState(EMPTY_FILTERS);
  const [statsA, setStatsA] = useState(null);
  const [statsB, setStatsB] = useState(null);
  const [loadingA, setLoadingA] = useState(false);
  const [loadingB, setLoadingB] = useState(false);
  const [paywall, setPaywall] = useState(null); // null | { plan, has_premium }
  // Race-guard refs: each stats fetch bumps its channel counter and only the
  // latest ticket writes to state. Prevents an older /studies/stats promise
  // from clobbering the current cohort when the user changes filters rapidly.
  const reqA = useRef(0);
  const reqB = useRef(0);

  useEffect(() => {
    (async () => {
      try {
        const { data: bs } = await api.get("/billing/status");
        if (!bs.has_premium) {
          setPaywall(bs);
          return;
        }
      } catch (err) { log.error("billing status", err); }
      try {
        const { data } = await api.get("/templates", { params: { include_deleted: true } });
        // Études = only practitioner's own templates (validated library excluded).
        const own = data.filter((t) => !t.is_validated);
        setTemplates(own);
        if (own.length) {
          const first = own[0].template_id;
          setFiltersA((f) => ({ ...f, template_id: first }));
          setFiltersB((f) => ({ ...f, template_id: own[Math.min(1, own.length - 1)].template_id }));
        }
      } catch (err) { log.error("templates load", err); }
    })();
    // Mount-only bootstrap. All referenced identifiers (`api`, `log`,
    // setState functions) are stable module or React refs and never change,
    // so `[]` is the correct dependency list here — the linter can't infer
    // stability of module imports.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!filtersA.template_id) return;
    const ticket = ++reqA.current;
    setLoadingA(true);
    fetchStats(filtersA)
      .then((res) => { if (ticket === reqA.current) setStatsA(res); })
      .catch((err) => {
        if (ticket !== reqA.current) return; // superseded — swallow late error
        log.error("stats A", err); toast.error("Erreur cohorte A"); setStatsA(null);
      })
      .finally(() => { if (ticket === reqA.current) setLoadingA(false); });
  }, [filtersA]);

  useEffect(() => {
    if (mode !== "compare" || !filtersB.template_id) { setStatsB(null); return; }
    const ticket = ++reqB.current;
    setLoadingB(true);
    fetchStats(filtersB)
      .then((res) => { if (ticket === reqB.current) setStatsB(res); })
      .catch((err) => {
        if (ticket !== reqB.current) return;
        log.error("stats B", err); toast.error("Erreur cohorte B"); setStatsB(null);
      })
      .finally(() => { if (ticket === reqB.current) setLoadingB(false); });
  }, [filtersB, mode]);

  const download = async (filters, name) => {
    if (!filters.template_id) return;
    try {
      const res = await fetch(exportCsvUrl(filters), { credentials: "include" });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name || res.headers.get("content-disposition")?.match(/filename="([^"]+)"/)?.[1] || "etude.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export CSV téléchargé");
    } catch (err) { log.error("csv export", err); toast.error("Export impossible"); }
  };

  const labelFor = (filters, stats) => {
    if (!stats) return "—";
    const parts = [stats.template.title];
    if (filters.date_from || filters.date_to) parts.push(`${filters.date_from || "…"} → ${filters.date_to || "…"}`);
    if (filters.sex && filters.sex !== "all") parts.push({ F: "F", M: "H", O: "Autre" }[filters.sex]);
    if (filters.age_from !== "" || filters.age_to !== "") parts.push(`${filters.age_from || 0}-${filters.age_to || "∞"} ans`);
    return parts.join(" · ");
  };

  const labels = useMemo(() => ({
    a: labelFor(filtersA, statsA),
    b: labelFor(filtersB, statsB),
  }), [filtersA, statsA, filtersB, statsB]);

  return (
    <Protected role={["admin", "research"]}>
      <AppShell title="Études cliniques">
        {paywall && (
          (() => {
            // The research assistant can't act on billing, so any blocked state
            // (trial expired OR cabinet still on Standard) shows a sober
            // "suspended" card that points them back to the practitioner.
            // The practitioner (admin) always sees the full Premium marketing
            // paywall — it's the one CTA that lets them unlock Études, whether
            // the block comes from an expired trial or from a Standard plan.
            const trialExpired =
              paywall.subscription_status === "trialing" &&
              paywall.trial_ends_at &&
              new Date(paywall.trial_ends_at) < new Date();
            if (isResearch) {
              return (
                <div className="bg-white border border-[#E1E9F0] rounded-2xl p-10 text-center shadow-[0_1px_3px_rgba(30,58,95,0.04)]" data-testid="studies-suspended">
                  <div className="mx-auto w-14 h-14 rounded-[14px] bg-[#F4F7FB] border border-[#E1E9F0] flex items-center justify-center mb-4">
                    <ChartLine size={26} weight="duotone" className="text-[#9AA9BC]" />
                  </div>
                  <h2 className="text-2xl font-bold tracking-tight text-[#1E3A5F] mb-2">Études temporairement suspendues</h2>
                  <p className="text-[#5F758F] max-w-lg mx-auto">
                    {trialExpired
                      ? "L'abonnement du cabinet est terminé. Les études cliniques seront à nouveau disponibles dès que le praticien titulaire aura souscrit un abonnement."
                      : "Cet espace est réservé aux cabinets abonnés à Premium. Contactez le praticien titulaire pour activer l'abonnement."}
                  </p>
                </div>
              );
            }
            return (
              <div className="bg-white border-2 border-[#3A8FA5] rounded-2xl p-10 text-center shadow-[0_1px_3px_rgba(30,58,95,0.04)]">
                <div className="mx-auto w-16 h-16 rounded-[14px] bg-[#E6F1F5] border border-[#C7DEE6] flex items-center justify-center mb-5">
                  <ChartLine size={32} weight="duotone" className="text-[#3A8FA5]" />
                </div>
                <h2 className="text-3xl font-bold tracking-tight text-[#1E3A5F] mb-2">Réservé à l'abonnement Premium</h2>
                <p className="text-[#5F758F] mb-6 max-w-lg mx-auto">
                  Les études cliniques (statistiques anonymisées, comparaison de cohortes, filtres démographiques, analyse des données de vos compte-rendus par IA, exports CSV) font partie de l'abonnement <strong>Premium à partir de 44,10€/mois TTC</strong>.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto mb-6 text-left">
                  {[
                    "Statistiques anonymisées",
                    "Comparaison de cohortes",
                    "Analyse des données de vos compte-rendus par IA",
                    "Accès assistant de recherche clinique",
                    "Exports CSV",
                  ].map((f) => (
                    <div key={f} className="flex items-start gap-2 text-sm">
                      <Sparkle size={16} weight="fill" className="text-[#3A8FA5] shrink-0 mt-0.5" />
                      <span className="text-[#1E3A5F]">{f}</span>
                    </div>
                  ))}
                </div>
                <Link to="/settings?tab=billing" data-testid="studies-upgrade-cta">
                  <Button className="rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] font-semibold text-white">
                    Passer en Premium
                  </Button>
                </Link>
              </div>
            );
          })()
        )}
        {!paywall && (
        <>
        <div className="bg-[#E6F1F5] border border-[#C7DEE6] rounded-2xl p-4 mb-6 text-sm text-[#1E3A5F]">
          <strong className="text-[#3A8FA5]">Analyse cohorte anonymisée.</strong> Les données sont agrégées sans identifier les patients — compatible RGPD Art. 5. Exportez en CSV pour analyse dans R, SPSS, Python ou Excel.
        </div>

        <Tabs value={mode} onValueChange={setMode} className="mb-4">
          <TabsList>
            <TabsTrigger value="single" data-testid="mode-single">Vue simple</TabsTrigger>
            <TabsTrigger value="compare" data-testid="mode-compare">Comparaison</TabsTrigger>
            <TabsTrigger value="correlation" data-testid="mode-correlation">
              <ChartLine size={14} className="mr-1.5" /> Corrélation
            </TabsTrigger>
            <TabsTrigger value="documents" data-testid="mode-documents">
              <FileMagnifyingGlass size={14} className="mr-1.5" /> Analyse de CR
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {mode === "documents" && <DocumentAnalyzer />}

        {mode === "correlation" && (
          <>
            <div className="bg-white border border-[#E1E9F0] rounded-2xl p-4 mb-6">
              <FiltersRow filters={filtersA} onChange={setFiltersA} templates={templates} />
            </div>
            <CorrelationView filters={filtersA} templates={templates} />
          </>
        )}

        {mode === "single" && (
          <>
            <div className="bg-white border border-[#E1E9F0] rounded-2xl p-4 mb-6">
              <FiltersRow filters={filtersA} onChange={setFiltersA} templates={templates} />
              <div className="mt-3 flex justify-end">
                <Button onClick={() => download(filtersA)} disabled={!statsA || statsA.count === 0} variant="outline" className="rounded-[10px] border-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white font-semibold" data-testid="studies-export-csv">
                  <DownloadSimple size={16} className="mr-2" /> Exporter CSV
                </Button>
              </div>
            </div>
            {loadingA && <p className="text-sm text-[#5F758F]">Calcul en cours…</p>}
            {!loadingA && <SingleView stats={statsA} templates={templates} tone="sage" />}
          </>
        )}

        {mode === "compare" && (
          <>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-6">
              <div className="bg-white border-2 border-[#3A8FA5]/40 rounded-2xl p-4">
                <FiltersRow filters={filtersA} onChange={setFiltersA} templates={templates} label="Cohorte A" dense />
                <div className="mt-3 flex justify-end">
                  <Button size="sm" onClick={() => download(filtersA, `cohorte-A-${Date.now()}.csv`)} disabled={!statsA || statsA.count === 0} variant="outline" className="rounded-md">
                    <DownloadSimple size={14} className="mr-1.5" /> CSV A
                  </Button>
                </div>
              </div>
              <div className="bg-white border-2 border-[#B9502C]/40 rounded-2xl p-4">
                <FiltersRow filters={filtersB} onChange={setFiltersB} templates={templates} label="Cohorte B" dense />
                <div className="mt-3 flex justify-end">
                  <Button size="sm" onClick={() => download(filtersB, `cohorte-B-${Date.now()}.csv`)} disabled={!statsB || statsB.count === 0} variant="outline" className="rounded-md">
                    <DownloadSimple size={14} className="mr-1.5" /> CSV B
                  </Button>
                </div>
              </div>
            </div>

            <ComparisonSummary a={statsA} b={statsB} labels={labels} />

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <div>
                <p className="text-xs tracking-[0.25em] uppercase text-[#3A8FA5] mb-3 font-medium">Cohorte A — {labels.a}</p>
                {loadingA ? <p className="text-sm text-[#5F758F]">Calcul…</p> : <SingleView stats={statsA} templates={templates} tone="sage" />}
              </div>
              <div>
                <p className="text-[11px] tracking-[0.12em] uppercase text-[#D45A3F] mb-3 font-bold">Cohorte B — {labels.b}</p>
                {loadingB ? <p className="text-sm text-[#5F758F]">Calcul…</p> : <SingleView stats={statsB} templates={templates} tone="terra" />}
              </div>
            </div>
          </>
        )}

        {mode === "single" && statsA && statsA.count > 0 && (
          <div className="bg-white border border-[#E1E9F0] rounded-2xl p-5 mt-6">
            <h3 className="text-xl font-bold text-[#1E3A5F] mb-4">Détail par question</h3>
            <div className="space-y-6">
              {statsA.per_question.map((q, idx) => (
                <div key={q.question_id} className="pb-6 border-b border-[#E1E9F0] last:border-b-0 last:pb-0">
                  <p className="text-sm font-medium text-[#1E3A5F] mb-1">
                    <span className="text-[#5F758F] mr-2">Q{idx + 1}.</span>{q.text}
                  </p>
                  <p className="text-xs text-[#5F758F] mb-3">{q.type} · {q.n} réponse{q.n > 1 ? "s" : ""}</p>
                  {q.distribution && q.distribution.length > 0 && (
                    <div className="space-y-1.5">
                      {q.distribution.map((d) => {
                        const pct = q.n > 0 ? Math.round((100 * d.count) / q.n) : 0;
                        return (
                          <div key={d.value} className="flex items-center gap-3 text-sm">
                            <div className="w-40 truncate text-[#1E3A5F]">{d.label}</div>
                            <div className="flex-1 h-2 bg-[#F4F7FB] rounded overflow-hidden">
                              <div className="h-full bg-[#3A8FA5]" style={{ width: `${pct}%` }} />
                            </div>
                            <div className="w-24 text-right text-[#5F758F] tabular-nums">{d.count} · {pct}%</div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {q.stats && (
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-sm">
                      {[
                        ["Moyenne", q.stats.mean], ["Médiane", q.stats.median], ["Écart-type", q.stats.stddev],
                        ["Min", q.stats.min], ["Max", q.stats.max], ["N", q.stats.n],
                      ].map(([label, val]) => (
                        <div key={label} className="bg-[#F4F7FB] rounded px-3 py-2">
                          <p className="text-xs text-[#5F758F]">{label}</p>
                          <p className="text-[#1E3A5F] font-medium">{val ?? "—"}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {!q.distribution && !q.stats && (
                    <p className="text-xs text-[#5F758F] italic">Question libre — texte non agrégé pour préserver la confidentialité.</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        </>
        )}
      </AppShell>
    </Protected>
  );
}
